import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Loader2, UserPlus } from "lucide-react";
import { apiError, recruitersAdminApi } from "../api";
import { Field, Modal } from "../ui";
import { useToast } from "@/app/providers/Toaster";
import { chileToday, PROGRAM_MIN_DATE } from "../format";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Alta de un reclutador. Recibe el rol "Reclutador" al instante si su correo
 * ya tiene cuenta; si no, la primera vez que entra a Nexus con Google.
 */
export function RegisterRecruiterModal({ onClose }: { onClose: () => void }) {
  const { push } = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [rut, setRut] = useState("");
  const [signedOn, setSignedOn] = useState("");
  const [notes, setNotes] = useState("");
  const [sendWelcome, setSendWelcome] = useState(true);

  const valid = name.trim().length >= 2 && EMAIL_RE.test(email.trim());
  const mut = useMutation({
    mutationFn: () =>
      recruitersAdminApi.create({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.trim() || undefined,
        rut: rut.trim() || undefined,
        agreementSignedOn: signedOn || undefined,
        internalNotes: notes.trim() || undefined,
        sendWelcome,
      }),
    onSuccess: (res) => {
      push({
        kind: "success",
        message: res.linked
          ? `${name.trim()} ya puede entrar a Nexus.`
          : `${name.trim()} quedó registrado: tendrá acceso cuando entre a Nexus con Google usando ${email.trim()}.`,
      });
      qc.invalidateQueries({ queryKey: ["recruiters"] });
      onClose();
      navigate(`/reclutadores/${res.recruiter.id}`);
    },
    onError: (err) => push({ kind: "error", message: apiError(err, "No pudimos registrar al reclutador.") }),
  });

  return (
    <Modal
      title="Registrar reclutador"
      icon={<UserPlus size={16} className="text-accent" />}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="nx-btn-ghost" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="nx-btn-primary" disabled={!valid || mut.isPending} onClick={() => mut.mutate()}>
            {mut.isPending && <Loader2 size={14} className="animate-spin" />}
            Registrar
          </button>
        </>
      }
    >
      <p className="text-sm text-fg-muted">
        Entra a Nexus con su cuenta de Google y sólo ve su espacio: cartera, checklist de sus eventos y ganancias.
      </p>
      <Field label="Nombre" required>
        <input className="nx-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre y apellido" autoFocus />
      </Field>
      <Field label="Correo" required hint="El mismo de su cuenta de Google.">
        <input className="nx-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nombre@correo.com" />
      </Field>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Teléfono">
          <input className="nx-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+56 9 …" />
        </Field>
        <Field label="RUT">
          <input className="nx-input" value={rut} onChange={(e) => setRut(e.target.value)} placeholder="12.345.678-5" />
        </Field>
      </div>
      <Field label="Firma del acuerdo" hint="Fecha en que firmó el Acuerdo de Reclutador Comercial.">
        <input className="nx-input" type="date" min={PROGRAM_MIN_DATE} max={chileToday()} autoComplete="off" value={signedOn} onChange={(e) => setSignedOn(e.target.value)} />
      </Field>
      <Field label="Notas internas" hint="Sólo las ve Mixcore.">
        <textarea className="nx-input min-h-[72px]" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>
      <label className="flex items-center gap-2 text-sm text-fg">
        <input type="checkbox" checked={sendWelcome} onChange={(e) => setSendWelcome(e.target.checked)} />
        Enviarle el correo de bienvenida con el link a Nexus
      </label>
    </Modal>
  );
}
